import { useState } from 'react';
import {
  AGES,
  BALANCE,
  TECHS_BY_ID,
  formatCount,
  formatDuration,
  formatNumber,
  getAge,
  quoteAge,
  quotePrestige,
} from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';

interface PrestigePanelProps {
  snapshot: Snapshot;
}

export function PrestigePanel({ snapshot }: PrestigePanelProps) {
  const [confirming, setConfirming] = useState<'fire' | 'age' | null>(null);
  const { state } = snapshot;
  const notation = state.settings.notation;

  const fire = quotePrestige(state);
  const age = quoteAge(state);
  const currentAge = getAge(state.ageIndex);
  const capstoneName = TECHS_BY_ID[currentAge.capstoneTech]?.name ?? currentAge.capstoneTech;

  const requestPass = (): void => {
    if (state.settings.confirmPrestige) setConfirming('fire');
    else gameStore.passFire();
  };

  const requestAdvance = (): void => {
    if (state.settings.confirmPrestige) setConfirming('age');
    else gameStore.advanceAge();
  };

  return (
    <>
      <section className="panel">
        <h2 className="panel-title">
          Pass the Fire
          <span className="hint">Generation {state.generation}</span>
        </h2>

        <div className="prestige-quote">
          <div>
            <span className="stat-label">Knowledge carried forward</span>
            <div className="prestige-figure">{formatNumber(fire.knowledge, notation)}</div>
          </div>
          <div>
            <span className="stat-label">Peak tribe</span>
            <div className="stat-value">
              {formatCount(fire.peakPopulation)} / {BALANCE.minimumPeakPopulationToPrestige} needed
            </div>
          </div>
          <div>
            <span className="stat-label">Discoveries held</span>
            <div className="stat-value">{fire.techsKnown}</div>
          </div>
        </div>

        <p className="card-desc">
          The old die and the young take up the work. Camp, discoveries and tribe are lost —
          Ancestral Memory is not.
        </p>

        {fire.blockedReason ? <p className="card-note">{fire.blockedReason}</p> : null}

        <div className="card-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className={`btn${fire.canPass ? ' primary' : ''}`}
            disabled={!fire.canPass}
            onClick={requestPass}
          >
            Pass the Fire
          </button>
        </div>
      </section>

      <section className="panel">
        <h2 className="panel-title">
          The Ages
          <span className="hint">
            {age.generationsDone} of {age.generationsRequired} generations in this age
          </span>
        </h2>

        <div className="age-track">
          {AGES.map((entry) => (
            <div
              key={entry.id}
              className={`age-pip${
                entry.index < state.ageIndex
                  ? ' done'
                  : entry.index === state.ageIndex
                    ? ' current'
                    : ''
              }`}
              title={entry.description}
            >
              {entry.name.replace('Age of ', '').replace('Age of the ', '')}
            </div>
          ))}
        </div>

        <p className="card-desc" style={{ marginTop: 10 }}>
          {currentAge.description}
        </p>

        <div className="card-effects" style={{ marginTop: 10 }}>
          <span className={`effect-chip${age.hasCapstone ? '' : ' cost short'}`}>
            Capstone: {capstoneName}
          </span>
          <span className="effect-chip">Era bonus ×{currentAge.eraMultiplier}</span>
          <span className="effect-chip">Rites held: {state.rites}</span>
        </div>

        {age.blockedReason ? <p className="card-note">{age.blockedReason}</p> : null}

        {state.completedAt !== null ? (
          <p className="card-flavor">
            The settlement was founded after {formatDuration(state.completedAt)} of play. The tribe
            keeps going regardless — there is always another fire.
          </p>
        ) : null}

        <div className="card-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className={`btn${age.canAdvance ? ' primary' : ''}`}
            disabled={!age.canAdvance}
            onClick={requestAdvance}
          >
            {age.isFinalAge ? 'Found the settlement' : 'Begin the next age'}
          </button>
        </div>
      </section>

      {confirming !== null ? (
        <div className="modal-backdrop" onClick={() => setConfirming(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h2>{confirming === 'fire' ? 'Pass the Fire?' : 'Move into the next age?'}</h2>
            <p>
              {confirming === 'fire'
                ? 'The generation ends. Camp, discoveries and tribe reset. Ancestral Memory endures.'
                : 'The tribe steps into a new era. Everything a generation reset takes, this takes too — and the world gets larger.'}
            </p>
            <p>
              Knowledge gained:{' '}
              <strong>{formatNumber(fire.knowledge, notation)}</strong>
            </p>

            <div className="modal-actions">
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  if (confirming === 'fire') gameStore.passFire();
                  else gameStore.advanceAge();
                  setConfirming(null);
                }}
              >
                Do it
              </button>
              <button type="button" className="btn" onClick={() => setConfirming(null)}>
                Not yet
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
